/**
 * The Execution field of DOR / LOR (CU-869fmm944, UX Obs. 1, 3, 9): one field, two kinds — Reports and Repricing —
 * shown as pills only to an account with DS Repricing. Pure rules; `ExecutionField.jsx` renders them.
 *
 * A pass is listed by `GET /api/repricing/analyzer/passes`: `{execution_id, slot, reason, strategies, searches,
 * finished, running, has_prices, offices}`. Only finished searches can be shown (gate 2, C3).
 */
import { format } from 'date-fns'
import { fmtSlot } from '../../../DsRepricing/Executions/@services/slot.js'

export const KIND = { REPORTS: 'reports', REPRICING: 'repricing' }

/** The pills: only with `ds_repricing` there is a second kind (decision 1: no change without it). */
export function executionKinds(userData) {
	return userData?.featuresV2?.ds_repricing
		? [{ value: KIND.REPORTS, label: 'Reports' }, { value: KIND.REPRICING, label: 'Repricing' }]
		: [{ value: KIND.REPORTS, label: 'Reports' }]
}

/** The kind the field opens on: the kind of what is open. */
export function openKind({ pass, report, kinds }) {
	if (pass && kinds.some(k => k.value === KIND.REPRICING)) return KIND.REPRICING
	if (report) return KIND.REPORTS
	return kinds[0]?.value || KIND.REPORTS
}

/** "scheduled" / "by hand", the words of Executions' Why chip. */
export const passWhy = pass => pass?.reason === 'force' ? 'by hand' : 'scheduled'

/** "27 Oct 2026 14:00", as Executions writes the pass's minute. */
export const passWhen = pass => fmtSlot(pass?.slot)

/** "Repricing pass · 27 Oct 2026 14:00", the value the field shows. */
export const passLabel = pass => pass ? `Repricing pass · ${passWhen(pass)}` : ''

export function passStrategies(pass) {
	const names = (pass?.strategies || []).map(s => s.name).filter(Boolean)
	return names.length ? names.join(', ') : 'no strategy recorded'
}

/** "340 searches" or "212 of 340 searches finished · running". */
export function passProgressLine(pass) {
	const total = pass?.searches || 0
	const finished = pass?.finished || 0
	if (!total) return 'no searches recorded'
	if (finished >= total) return `${total} ${total === 1 ? 'search' : 'searches'}`
	return `${finished} of ${total} searches finished · running`
}

/** Why a pass cannot be picked, or null. */
export function passDisabledReason(pass) {
	if (!pass) return null
	if (!pass.searches) return 'no prices to show'
	if (!pass.has_prices) return 'no prices yet — no search has finished'
	return null
}

const dayKey = date => format(date, 'yyyy-MM-dd')

/** "Today" / "Yesterday" / "26 Oct 2026", in the browser's time like the minute of the pass. */
export function dayGroup(slotIso, now = new Date()) {
	const d = new Date(slotIso)
	if (Number.isNaN(d.getTime())) return '—'
	const yesterday = new Date(now)
	yesterday.setDate(now.getDate() - 1)
	if (dayKey(d) === dayKey(now)) return 'Today'
	if (dayKey(d) === dayKey(yesterday)) return 'Yesterday'
	return format(d, 'dd MMM yyyy')
}

/** The list as the popover draws it: newest first, grouped by day; `query` matches a strategy, the day or the time. */
export function groupPasses(passes, { query = '', now = new Date() } = {}) {
	const q = String(query || '').trim().toLowerCase()
	const matches = pass => !q || [passStrategies(pass), passWhen(pass), dayGroup(pass.slot, now), passWhy(pass)]
		.some(text => String(text).toLowerCase().includes(q))
	const sorted = [...(passes || [])].filter(matches).sort((a, b) => String(b.slot).localeCompare(String(a.slot)))
	const groups = []
	for (const pass of sorted) {
		const day = dayGroup(pass.slot, now)
		const last = groups[groups.length - 1]
		if (last && last.day === day) last.items.push(pass)
		else groups.push({ day, items: [pass] })
	}
	return groups
}

/** The offices of a pass, as the Office field lists them (only those with a finished search). */
export function passOfficeOptions(pass) {
	return (pass?.offices || []).filter(o => o.finished > 0).map(o => ({ label: o.label, value: o.value }))
}

/** "This pass is still running: 212 of 340 searches finished." or null. */
export function runningNote(pass) {
	if (!pass?.running) return null
	return `This pass is still running: ${pass.finished} of ${pass.searches} searches finished. Dates and lengths still searching are not in the table yet.`
}

/** The ids the analyzer body carries: one kind at a time. */
export function executionIds({ report, pass }) {
	return pass
		? { reportExecutionID: null, repricingExecutionID: pass }
		: { reportExecutionID: report || null, repricingExecutionID: null }
}

/** Opened by link (`idRepricingExecution` / `idReportExecution`) and not in the lists once they loaded → not found. */
export function linkNotFound({ pass, passes, passesLoaded, report, reports, reportsLoaded }) {
	if (pass) return !!passesLoaded && !(passes || []).some(p => p.execution_id === pass)
	if (report) return !!reportsLoaded && !(reports || []).some(r => r.value === report)
	return false
}
